import React, { useEffect, useMemo, useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Button,
  Input,
  Label,
  FormGroup,
  Spinner,
} from "reactstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Request from "../../../../helpers/request";
import * as url from "../../../../helpers/url_helper";

// Adjust these to match your real device protocol values.
const EXECUTE_VALUE_ON = "XX1";
const EXECUTE_VALUE_OFF = "XX0";

// Confirmed curtain protocol values.
const CURTAIN_OPEN_VALUE = "01001";
const CURTAIN_CLOSE_VALUE = "01021";

const DRIVER_TEMP_MIN = 2700;
const DRIVER_TEMP_MAX = 6500;
const DRIVER_TEMP_STEP = 100;
const DRIVER_TEMP_DEFAULT = 4000;

const DRIVER_BRIGHTNESS_MIN = 1;
const DRIVER_BRIGHTNESS_MAX = 100;
const DRIVER_BRIGHTNESS_DEFAULT = 50;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

// the temperature slider
const colorTemperatureToHex = (kelvinInput: number): string => {
  const kelvin = clamp(kelvinInput, 1000, 40000);
  const temp = kelvin / 100;

  let r: number;
  let g: number;
  let b: number;

  if (temp <= 66) {
    r = 255;
    g = 99.4708025861 * Math.log(temp) - 161.1195681661;
    b = temp <= 19 ? 0 : 138.5177312231 * Math.log(temp - 10) - 305.0447927307;
  } else {
    r = 329.698727446 * Math.pow(temp - 60, -0.1332047592);
    g = 288.1221695283 * Math.pow(temp - 60, -0.0755148492);
    b = 255;
  }

  const toHex = (channel: number) =>
    Math.round(clamp(channel, 0, 255))
      .toString(16)
      .toUpperCase()
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Maps each tab to the id field name the create/update endpoints expect.
// "driver" is a guess (airDriverId) — confirm against a real payload.
const ID_FIELD_BY_TAB: { [key: string]: string } = {
  chip: "chipSwitchId",
  scene: "sceneId",
  curtain: "airCurtainId",
  driver: "airDriverId",
};

// Scenes have no on/off state — executeValue is always sent as "".
const TABS_WITHOUT_ACTION = ["scene"];

const PERCENT_BY_LEVEL: { [key: number]: number } = {
  1: 25,
  2: 50,
  3: 75,
  4: 100,
};
const LEVEL_CONTROLLED_SWITCH_TYPES = [1, 2];

const percentageToHex = (pct: number) =>
  pct.toString(16).toUpperCase().padStart(2, "0");

const buildLevelExecuteValue = (
  level: number,
  isOn: boolean,
  switchType?: number,
) => {
  // Dimmer (type 2): level is directly 10–100
  if (switchType === 2) {
    const safeLevel = Math.min(100, Math.max(10, level));
    return `${safeLevel.toString(16).toUpperCase().padStart(2, "0")}${
      isOn ? "1" : "0"
    }`;
  }

  // Fan (type 1): existing 1–4 level logic
  return `${percentageToHex(PERCENT_BY_LEVEL[level] ?? 25)}${isOn ? "1" : "0"}`;
};
// Parses a stored level executeValue (e.g. "191") back into { level, on }.
const parseLevelExecuteValue = (
  value?: string,
): { level: number; on: boolean } | null => {
  if (!value || value.length < 3) return null;
  const hexPart = value.slice(0, -1);
  const onOffPart = value.slice(-1);
  const pct = parseInt(hexPart, 16);
  if (Number.isNaN(pct)) return null;
  const match = Object.entries(PERCENT_BY_LEVEL).find(([, p]) => p === pct);
  return {
    level: match ? parseInt(match[0], 10) : 1,
    on: onOffPart === "1",
  };
};

// Curtain "Precise Control" executeValue: "02" + 2-digit hex percent + "1".

const buildCurtainRangeExecuteValue = (percent: number) =>
  `02${percentageToHex(percent)}1`;

const parseCurtainRangeExecuteValue = (value?: string): number | null => {
  if (!value || !value.startsWith("02") || value.length < 4) return null;
  const hexPart = value.slice(2, -1);
  const pct = parseInt(hexPart, 16);
  return Number.isNaN(pct) ? null : pct;
};

const DAY_OPTIONS = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
];

const DEVICE_TYPE_TABS = [
  { key: "chip", label: "Chip" },
  { key: "scene", label: "Scene" },
  { key: "curtain", label: "Curtain" },
  { key: "driver", label: "Driver" },
];

// Each tab is backed by its own endpoint/collection — not a filter on one list.
const TAB_ENDPOINTS: { [key: string]: string } = {
  chip: url.GET_CHIPS_LIST,
  scene: url.GET_SCENE_LIST,
  curtain: url.GET_CURTAIN_LIST,
  driver: url.GET_GATEWAY_LIST,
};

const IST_OFFSET_MINUTES = 5 * 60 + 30;

const hhmmssToSeconds = (time: string) => {
  const [h, m, s] = time.split(":").map((n) => parseInt(n, 10));

  return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
};

const secondsToHHmmss = (totalSeconds: number) => {
  const normalized =
    ((totalSeconds % 86400) + 86400) % 86400;

  const h = Math.floor(normalized / 3600);
  const m = Math.floor((normalized % 3600) / 60);
  const s = normalized % 60;

  return `${String(h).padStart(2, "0")}:${String(m).padStart(
    2,
    "0",
  )}:${String(s).padStart(2, "0")}`;
};

const istToUtcHHmmss = (istTime: string) =>
  secondsToHHmmss(
    hhmmssToSeconds(istTime) - IST_OFFSET_MINUTES * 60,
  );

const utcToIstHHmmss = (utcTime: string) =>
  secondsToHHmmss(
    hhmmssToSeconds(utcTime) + IST_OFFSET_MINUTES * 60,
  );
// Parses a 12-hour display string like "3:58 PM" into 24-hour "15:58"
const parse12HourToHHmm = (value: string): string | null => {
  const match = value?.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  const [, hh, mm, meridiem] = match;
  let hours = parseInt(hh, 10) % 12;
  if (meridiem.toUpperCase() === "PM") hours += 12;
  return `${String(hours).padStart(2, "0")}:${mm}`;
};

// Default the time picker to "now" so it never renders the empty "--:-- --" state.
const getCurrentHHmmss = () => {
  const now = new Date();

  return `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes(),
  ).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
};

export interface ChipSwitch {
  _id: string;
  button?: string;
  displayName?: string;
  type?: number;
}

export interface ChipItem {
  id?: string;
  slave: string;
  roomName?: string;
  modelType?: string;
  isOnline?: boolean;
  switches?: ChipSwitch[];
  [key: string]: any;
}

export interface DeviceTypeItem {
  id?: string;
  _id?: string;
  slave?: string;
  name?: string;
  displayName?: string;
  roomName?: string;
  switches?: ChipSwitch[]; // only present for "chip" tab items
  // Driver-specific fields (from gateway.drivers[])
  brightness?: number;
  temp?: number;
  isOn?: boolean;
  isDimmable?: boolean;
  isTunable?: boolean;
  gatewayId?: string;
  [key: string]: any;
}

export interface ScheduleRow {
  _id?: string;
  scheduleId?: string;
  chipSwitchId?: string;
  airDriverId?: string;
  slave?: string;
  deviceName?: string;
  deviceType?: string;
  executeValue?: string;
  // Driver (light) schedules use separate fields instead of executeValue.
  temp?: number | string;
  brightness?: number | string;
  status?: number | string;
  color?: string;
  dayNumbers?: number[];
  time?: string;
  istTime?: string;
  isRepeat?: boolean;
  isActive?: boolean;
}

interface ScheduleFormOffcanvasProps {
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
  userId: any;
  selectedPremise?: any;
  editingSchedule?: ScheduleRow | null;
}

const handleUnauthorized = () => {
  localStorage.removeItem("userInfo");
  localStorage.removeItem("selectedPremiseID");
  localStorage.removeItem("selectedUserID");
  localStorage.removeItem("selectedUserEmail");
  localStorage.removeItem("Authorization");
  localStorage.removeItem("permissions");
  window.location.reload();
};

const extractErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.msg?.message ||
  error?.response?.data?.msg ||
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  fallback;

const resolveExecuteValue = (tab: string, action: "on" | "off") => {
  if (TABS_WITHOUT_ACTION.includes(tab)) return "";
  if (tab === "curtain")
    return action === "on" ? CURTAIN_OPEN_VALUE : CURTAIN_CLOSE_VALUE;
  return action === "on" ? EXECUTE_VALUE_ON : EXECUTE_VALUE_OFF;
};

const inferTabFromRow = (row?: ScheduleRow | null): string => {
  const type = (row?.deviceType || "").toLowerCase();
  if (type.includes("scene")) return "scene";
  if (type.includes("curtain")) return "curtain";
  if (type.includes("driver")) return "driver";
  return "chip";
};

// Gateway list response nests the actual controllable light drivers inside
const flattenGatewayDrivers = (gateways: any[]): DeviceTypeItem[] =>
  (gateways || []).flatMap((gateway: any) =>
    (gateway.drivers || []).map((driver: any) => ({
      ...driver,
      id: driver.id ?? driver._id,
      slave: driver.slave ?? gateway.slave,
      roomName: driver.roomName,
      gatewayId: gateway.id ?? gateway._id,
    })),
  );

const ScheduleFormOffcanvas = ({
  show,
  onClose,
  onSaved,
  userId,
  selectedPremise,
  editingSchedule,
}: ScheduleFormOffcanvasProps) => {
  const isEditMode = !!editingSchedule?.scheduleId || !!editingSchedule?._id;

  const [tabItemsCache, setTabItemsCache] = useState<{
    [key: string]: DeviceTypeItem[];
  }>({});
  const [tabLoading, setTabLoading] = useState<boolean>(false);

  const [selectedChipKey, setSelectedChipKey] = useState<string>("");
  const [chipSwitchId, setChipSwitchId] = useState<string>("");
  const [deviceTypeTab, setDeviceTypeTab] = useState<string>("chip");
  const [action, setAction] = useState<"on" | "off">("on");
  const [level, setLevel] = useState<number>(1);
  const [time, setTime] = useState<string>("");
  const [isRepeat, setIsRepeat] = useState<boolean>(true);
  const [dayNumbers, setDayNumbers] = useState<number[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  //curtain control
  const [curtainControl, setCurtainControl] = useState<"quick" | "control">(
    "quick",
  );
  const [curtainLevel, setCurtainLevel] = useState<number>(50);
  // driver (light) control — brightness 1-100, color temperature 2700K-6500K
  const [driverBrightness, setDriverBrightness] = useState<number>(
    DRIVER_BRIGHTNESS_DEFAULT,
  );
  const [driverTemp, setDriverTemp] = useState<number>(DRIVER_TEMP_DEFAULT);
  // Derived, not user-editable — the RGB color matching the chosen color
  // temperature, computed with the blackbody-radiation approximation.
  const driverColor = useMemo(
    () => colorTemperatureToHex(driverTemp),
    [driverTemp],
  );
  // Same global room list Device Binding uses. We always want "all devices" here
  // (no per-room filter UI), so we build the roomId as if every room were checked.
  const roomListNew = useSelector(
    (state: any) => state.APIResponse.roomListNew,
  );
  const allRoomsId = useMemo(() => {
    const roomIds = (roomListNew || []).map((r: any) => r.id).filter(Boolean);
    return [...roomIds].join(",");
  }, [roomListNew]);
  const currentTabItems = tabItemsCache[deviceTypeTab] || [];

  const selectedItem = useMemo(
    () =>
      currentTabItems.find(
        (c) => (c.id ?? c._id ?? c.slave) === selectedChipKey,
      ),
    [currentTabItems, selectedChipKey],
  );

  // Only relevant for the "chip" tab — the specific switch within the device,
  // which tells us whether it's a Dimmer/Fan (level-controlled) or plain switch.
  const selectedSwitchObj = useMemo(() => {
    if (deviceTypeTab !== "chip" || !selectedItem) return null;
    return (
      (selectedItem.switches || []).find((sw) => sw._id === chipSwitchId) ||
      null
    );
  }, [deviceTypeTab, selectedItem, chipSwitchId]);

  const isLevelControlled =
    !!selectedSwitchObj &&
    LEVEL_CONTROLLED_SWITCH_TYPES.includes(selectedSwitchObj.type ?? 0);

  // Fetch the active tab's items (chips/scenes/curtains/drivers) whenever the
  // panel is open and this tab hasn't been loaded yet.
  useEffect(() => {
    if (!show) return;
    if (tabItemsCache[deviceTypeTab] !== undefined) return; // already cached
    let cancelled = false;

    const fetchTabItems = async () => {
      setTabLoading(true);
      try {
        const reqObj = {
          draw: 1,
          order: [],
          start: 0,
          length: 1000,
          search: { value: "", regex: false },
          userId,
          ...(selectedPremise?.id ? { premiseId: selectedPremise.id } : {}),
          roomId: allRoomsId,
        };

        const endpoint = TAB_ENDPOINTS[deviceTypeTab];
        const response = await Request.post(
          `${url.BASE_URL}${endpoint}`,
          reqObj,
        );

        if (cancelled) return;

        const rawItems = response.data?.data || [];
        // "driver" tab: the gateway list response nests the actual drivers —
        // flatten those out so the dropdown lists individual lights, not gateways.
        const items =
          deviceTypeTab === "driver"
            ? flattenGatewayDrivers(rawItems)
            : rawItems;

        setTabItemsCache((prev) => ({
          ...prev,
          [deviceTypeTab]: items,
        }));
      } catch (error: any) {
        if (cancelled) return;
        console.error(`Error fetching ${deviceTypeTab} list:`, error);
        toast.error(`Failed to load ${deviceTypeTab} list`);
        setTabItemsCache((prev) => ({ ...prev, [deviceTypeTab]: [] }));
        if (error?.response?.status === 401) handleUnauthorized();
      } finally {
        if (!cancelled) setTabLoading(false);
      }
    };

    fetchTabItems();
    return () => {
      cancelled = true;
    };
  }, [show, deviceTypeTab, userId, selectedPremise, allRoomsId, tabItemsCache]);

  // Prefill when editing, reset when creating
  useEffect(() => {
    if (!show) return;

    if (editingSchedule) {
      const inferredTab = inferTabFromRow(editingSchedule);
      setDeviceTypeTab(inferredTab);
      setChipSwitchId(editingSchedule.chipSwitchId || "");

      if (inferredTab === "curtain") {
        const val = editingSchedule.executeValue;
        if (val && val.startsWith("02")) {
          const pct = parseCurtainRangeExecuteValue(val);
          setCurtainControl("control");
          setCurtainLevel(pct ?? 50);
          setAction("on");
        } else {
          setCurtainControl("quick");
          setAction(val === CURTAIN_CLOSE_VALUE ? "off" : "on");
        }
      } else if (inferredTab === "driver") {
        const parsedTemp = Number(editingSchedule.temp);
        const parsedBrightness = Number(editingSchedule.brightness);
        setDriverTemp(
          Number.isFinite(parsedTemp) ? parsedTemp : DRIVER_TEMP_DEFAULT,
        );
        setDriverBrightness(
          Number.isFinite(parsedBrightness)
            ? parsedBrightness
            : DRIVER_BRIGHTNESS_DEFAULT,
        );
        setAction(String(editingSchedule.status) === "0" ? "off" : "on");
      } else {
        setAction(
          editingSchedule.executeValue === EXECUTE_VALUE_OFF ? "off" : "on",
        );
      }

      const fromIstLabel = editingSchedule.istTime
        ? parse12HourToHHmm(editingSchedule.istTime)
        : null;
        // console.log("time",editingSchedule.istTime )
      const fallbackFromUtc = editingSchedule.time
        ? utcToIstHHmmss(editingSchedule.time.slice(0, 5))
        : "";
      setTime(fromIstLabel || fallbackFromUtc || getCurrentHHmmss());

      setIsRepeat(!!editingSchedule.isRepeat);
      setDayNumbers(editingSchedule.dayNumbers || []);
      setIsActive(editingSchedule.isActive ?? true);
    } else {
      setSelectedChipKey("");
      setChipSwitchId("");
      setDeviceTypeTab("chip");
      setTabItemsCache({});
      setAction("on");
      setLevel(1);
      setCurtainControl("quick");
      setCurtainLevel(50);
      setDriverBrightness(DRIVER_BRIGHTNESS_DEFAULT);
      setDriverTemp(DRIVER_TEMP_DEFAULT);
      setTime(getCurrentHHmmss());
      setIsRepeat(true);
      setDayNumbers([]);
      setIsActive(true);
    }
    setErrors({});
  }, [show, editingSchedule]);

  // In edit mode, once the chip list loads, find which chip owns this switch
  // so selectedSwitchObj (and therefore isLevelControlled) can resolve.
  useEffect(() => {
    if (!show || !isEditMode || deviceTypeTab !== "chip") return;
    if (!editingSchedule?.chipSwitchId || selectedChipKey) return;

    const items = tabItemsCache["chip"] || [];
    const matchChip = items.find((c) =>
      (c.switches || []).some((sw) => sw._id === editingSchedule.chipSwitchId),
    );
    if (matchChip) {
      setSelectedChipKey(
        matchChip.id ?? matchChip._id ?? matchChip.slave ?? "",
      );
    }
  }, [
    show,
    isEditMode,
    deviceTypeTab,
    editingSchedule,
    tabItemsCache,
    selectedChipKey,
  ]);

  // Once we know the switch is level-controlled, parse the stored executeValue
  // into { level, on } instead of the plain on/off interpretation.
  useEffect(() => {
    if (
      !show ||
      !isEditMode ||
      !isLevelControlled ||
      !editingSchedule?.executeValue
    )
      return;
    const parsed = parseLevelExecuteValue(editingSchedule.executeValue);
    if (parsed) {
      setLevel(parsed.level);
      setAction(parsed.on ? "on" : "off");
    }
  }, [show, isEditMode, isLevelControlled, editingSchedule]);

  const toggleDay = (value: number) => {
    setDayNumbers((prev) =>
      prev.includes(value)
        ? prev.filter((d) => d !== value)
        : [...prev, value].sort(),
    );
  };

  const handleTabChange = (tabKey: string) => {
    setDeviceTypeTab(tabKey);
    setSelectedChipKey("");
    setChipSwitchId("");
    setLevel(1);
    setCurtainControl("quick");
    setCurtainLevel(50);
    setDriverBrightness(DRIVER_BRIGHTNESS_DEFAULT);
    setDriverTemp(DRIVER_TEMP_DEFAULT);
  };

  const handleDeviceSelect = (value: string) => {
    setSelectedChipKey(value);
    setLevel(1);
    if (deviceTypeTab === "chip") {
      // Chip devices have a nested switches list — user still needs to pick one.
      setChipSwitchId("");
    } else {
      // Scenes/curtains/drivers are flat items — the item itself is the target.
      setChipSwitchId(value);

      if (deviceTypeTab === "driver") {
        // Prefill brightness/temp sliders from the driver's current state.
        const driverItem = currentTabItems.find(
          (c) => (c.id ?? c._id ?? c.slave) === value,
        );
        setDriverBrightness(
          driverItem?.brightness || DRIVER_BRIGHTNESS_DEFAULT,
        );
        setDriverTemp(driverItem?.temp || DRIVER_TEMP_DEFAULT);
        setAction(driverItem?.isOn ? "on" : "off");
      }
    }
  };

  const validate = () => {
    const nextErrors: { [key: string]: string } = {};
    if (!isEditMode && !chipSwitchId)
      nextErrors.chipSwitchId = "Please select a switch";
    if (!time) nextErrors.time = "Please select a time";
    if (!dayNumbers.length)
      nextErrors.dayNumbers = "Please select at least one day";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);

    try {
      const scheduleId = editingSchedule?.scheduleId || editingSchedule?._id;
      const sharedFields = {
        dayNumbers,
        time: istToUtcHHmmss(time),
        isRepeat,
        isActive,
      };

      if (deviceTypeTab === "driver") {
        const driverFields = {
          temp: String(driverTemp),
          brightness: String(driverBrightness),
          status: action === "on" ? "1" : "0",
          color: driverColor,
        };

        if (isEditMode) {
          await Request.put(`${url.BASE_URL}${url.UPDATE_USER_SCHEDULE}`, {
            scheduleId,
            ...driverFields,
            ...sharedFields,
          });
          toast.success("Schedule updated successfully");
        } else {
          await Request.post(`${url.BASE_URL}${url.ADD_USER_SCHEDULE}`, {
            userId,
            airDriverId: chipSwitchId,
            ...driverFields,
            ...sharedFields,
          });
          toast.success("Schedule created successfully");
        }
      } else {
        const executeValue = isLevelControlled
          ? buildLevelExecuteValue(
              level,
              action === "on",
              selectedSwitchObj?.type,
            )
          : deviceTypeTab === "curtain"
          ? curtainControl === "control"
            ? buildCurtainRangeExecuteValue(curtainLevel)
            : action === "on"
            ? CURTAIN_OPEN_VALUE
            : CURTAIN_CLOSE_VALUE
          : resolveExecuteValue(deviceTypeTab, action);

        if (isEditMode) {
          await Request.put(`${url.BASE_URL}${url.UPDATE_USER_SCHEDULE}`, {
            scheduleId,
            executeValue,
            ...sharedFields,
          });
          toast.success("Schedule updated successfully");
        } else {
          const idField = ID_FIELD_BY_TAB[deviceTypeTab] || "chipSwitchId";
          await Request.post(`${url.BASE_URL}${url.ADD_USER_SCHEDULE}`, {
            userId,
            [idField]: chipSwitchId,
            executeValue,
            ...sharedFields,
          });
          toast.success("Schedule created successfully");
        }
      }
      onSaved();
      onClose();
    } catch (error: any) {
      console.error("Error saving schedule:", error);
      toast.error(extractErrorMessage(error, "Something went wrong"));
      if (error?.response?.status === 401) handleUnauthorized();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Offcanvas
        isOpen={show}
        toggle={onClose}
        direction="end"
        style={{ width: 420 }}
      >
        <OffcanvasHeader
          toggle={onClose}
          className="d-flex align-items-center justify-content-between w-100"
        >
          <h5 className="mb-0 fw-bold fs-3">
            {isEditMode ? "Edit Schedule" : "Create Schedule"}
          </h5>
        </OffcanvasHeader>

        <OffcanvasBody className="d-flex flex-column p-0">
          <form
            onSubmit={handleSubmit}
            className="d-flex flex-column flex-grow-1"
            style={{ minHeight: 0 }}
          >
            <div className="flex-grow-1 overflow-auto p-3">
              {!isEditMode && (
                <div className="mb-3 ml-8">
                  <div
                    className="d-inline-flex align-items-center "
                    style={{
                      backgroundColor: "#ececec",
                      borderRadius: 999,
                      padding: 5,
                      gap: 10,
                    }}
                  >
                    {DEVICE_TYPE_TABS.map((tab) => {
                      const active = deviceTypeTab === tab.key;
                      return (
                        <button
                          type="button"
                          key={tab.key}
                          onClick={() => handleTabChange(tab.key)}
                          style={{
                            border: "none",
                            borderRadius: 999,
                            padding: "8px 20px",
                            fontSize: 13,
                            fontWeight: active ? 700 : 500,
                            backgroundColor: active ? "#1c1c1e" : "transparent",
                            color: active ? "#ffffff" : "#6b7280",
                            transition: "background-color 0.15s, color 0.15s",
                            cursor: "pointer",
                          }}
                        >
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {isEditMode ? (
                <>
                  <FormGroup>
                    <Label className="fw-semibold">
                      {DEVICE_TYPE_TABS.find((t) => t.key === deviceTypeTab)
                        ?.label || "Item"}
                    </Label>
                    <div className="form-control bg-light text-muted">
                      {editingSchedule?.slave ||
                        editingSchedule?.deviceName ||
                        "-"}
                    </div>
                  </FormGroup>
                </>
              ) : (
                <>
                  {/* Step 1: pick the item from the active tab's list */}
                  <FormGroup>
                    <Label className="fw-semibold">
                      {
                        DEVICE_TYPE_TABS.find((t) => t.key === deviceTypeTab)
                          ?.label
                      }
                    </Label>
                    <Input
                      type="select"
                      value={selectedChipKey}
                      onChange={(e) => handleDeviceSelect(e.target.value)}
                      disabled={tabLoading}
                      invalid={
                        deviceTypeTab !== "chip" && !!errors.chipSwitchId
                      }
                    >
                      <option value="">
                        {tabLoading ? "Loading..." : "Select a device "}
                      </option>
                      {currentTabItems.map((item) => {
                        const key = item.id ?? item._id ?? item.slave;
                        const label =
                          deviceTypeTab === "chip"
                            ? `${item.slave}${
                                item.roomName ? ` • ${item.roomName} • ${item.modelType} ` : ""
                              }`
                            : deviceTypeTab === "driver"
                            ? `${item.name || item.slave}${
                                item.roomName ? ` • ${item.roomName} ${item?.isOnline ? "🟢" : "🔴"} ` : ""
                              }`
                            : item.name ||
                              item.displayName ||
                              item.slave ||
                              key;
                        return (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        );
                      })}
                    </Input>
                    {!tabLoading && currentTabItems.length === 0 && (
                      <div className="text-muted font-size-11 mt-1">
                        No{" "}
                        {DEVICE_TYPE_TABS.find(
                          (t) => t.key === deviceTypeTab,
                        )?.label.toLowerCase()}{" "}
                        items found for this user/premise.
                      </div>
                    )}
                    {deviceTypeTab !== "chip" && errors.chipSwitchId && (
                      <div className="text-danger font-size-11 mt-1">
                        {errors.chipSwitchId}
                      </div>
                    )}
                  </FormGroup>

                  {/* Step 2: chip devices need a specific switch picked too */}
                  {deviceTypeTab === "chip" && selectedItem && (
                    <FormGroup>
                      <Label className="fw-semibold">Switch</Label>
                      <Input
                        type="select"
                        value={chipSwitchId}
                        onChange={(e) => {
                          setChipSwitchId(e.target.value);
                          setLevel(1);
                        }}
                        invalid={!!errors.chipSwitchId}
                      >
                        <option value="">Select a switch</option>
                        {(selectedItem.switches || []).map((sw) => (
                          <option key={sw._id} value={sw._id}>
                            {sw.displayName || `Switch ${sw.button}`}
                          </option>
                        ))}
                      </Input>
                      {!selectedItem.switches?.length && (
                        <div className="text-muted font-size-11 mt-1">
                          This device has no switches.
                        </div>
                      )}
                      {errors.chipSwitchId && (
                        <div className="text-danger font-size-11 mt-1">
                          {errors.chipSwitchId}
                        </div>
                      )}
                    </FormGroup>
                  )}
                </>
              )}

              {/* Level picker — only for Dimmer/Fan switch types */}
              {isLevelControlled && (
                <FormGroup>
                  {selectedSwitchObj?.type === 2 ? (
                    // DIMMER
                    <>
                      <Label className="fw-semibold d-block">
                        Light Dimming
                      </Label>
                      <Input
                        type="range"
                        min="10"
                        max="100"
                        step="10"
                        value={level}
                        onChange={(e) => setLevel(Number(e.target.value))}
                      />

                      <div className="d-flex justify-content-between mt-2">
                        <span>10%</span>

                        <strong>{level}%</strong>

                        <span>100%</span>
                      </div>
                    </>
                  ) : (
                    // FAN
                    <>
                      <Label className="fw-semibold d-block">Fan Speed</Label>
                      <div className="d-flex flex-wrap gap-2">
                        {[1, 2, 3, 4].map((lvl) => {
                          const active = level === lvl;

                          return (
                            <button
                              type="button"
                              key={lvl}
                              onClick={() => setLevel(lvl)}
                              className={`btn btn-sm ${
                                active ? "btn-dark" : "btn-outline-secondary"
                              } fw-bold`}
                              style={{
                                minWidth: 64,
                                borderRadius: 20,
                              }}
                            >
                              {lvl}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </FormGroup>
              )}

              {/* Driver (light) controls — brightness + color temperature */}
              {deviceTypeTab === "driver" && (isEditMode || selectedItem) && (
                <>
                  <FormGroup>
                    <Label className="fw-semibold d-block">Brightness</Label>
                    <Input
                      type="range"
                      min={DRIVER_BRIGHTNESS_MIN}
                      max={DRIVER_BRIGHTNESS_MAX}
                      step="1"
                      value={driverBrightness}
                      onChange={(e) =>
                        setDriverBrightness(Number(e.target.value))
                      }
                      style={{ accentColor: "#1c1c1e" }}
                    />
                    <div className="d-flex justify-content-between mt-2">
                      <span>{DRIVER_BRIGHTNESS_MIN}%</span>
                      <strong>{driverBrightness}%</strong>
                      <span>{DRIVER_BRIGHTNESS_MAX}%</span>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label className="fw-semibold d-block">
                      Color Temperature
                    </Label>
                    <Input
                      type="range"
                      min={DRIVER_TEMP_MIN}
                      max={DRIVER_TEMP_MAX}
                      step={DRIVER_TEMP_STEP}
                      value={driverTemp}
                      onChange={(e) => setDriverTemp(Number(e.target.value))}
                      style={{ accentColor: "#1c1c1e" }}
                    />
                    <div className="d-flex justify-content-between mt-2">
                      <span>{DRIVER_TEMP_MIN}K</span>
                      <strong>{driverTemp}K</strong>
                      <span>{DRIVER_TEMP_MAX}K</span>
                    </div>
                  </FormGroup>
                </>
              )}

              {/* On/Off action toggle — hidden for tabs with no action (e.g. scenes) */}
              {/* Curtain Access Control */}
              {deviceTypeTab === "curtain" ? (
                <FormGroup>
                  <Label className="fw-semibold d-block mb-2">
                    Curtain Access
                  </Label>

                  {/* Access Type */}
                  <div className="d-flex align-items-center gap-4">
                    {/* Quick Access */}
                    <div className="form-check">
                      <Input
                        type="checkbox"
                        id="quickAccess"
                        checked={curtainControl === "quick"}
                        onChange={() => {
                          setCurtainControl("quick");
                        }}
                      />

                      <Label
                        for="quickAccess"
                        className="form-check-label fw-semibold"
                        style={{ cursor: "pointer" }}
                      >
                        Quick Access
                      </Label>
                    </div>

                    {/* Control Access */}
                    <div className="form-check">
                      <Input
                        type="checkbox"
                        id="controlAccess"
                        checked={curtainControl === "control"}
                        onChange={() => {
                          setCurtainControl("control");
                        }}
                      />

                      <Label
                        for="controlAccess"
                        className="form-check-label fw-semibold"
                        style={{ cursor: "pointer" }}
                      >
                        Precise Control
                      </Label>
                    </div>
                  </div>

                  {/* Quick Access */}
                  {curtainControl === "quick" && (
                    <div className="d-flex gap-2 mt-3">
                      <button
                        type="button"
                        className={`btn flex-fill fw-bold ${
                          action === "on" ? "btn-dark" : "btn-outline-secondary"
                        }`}
                        onClick={() => setAction("on")}
                        style={{ borderRadius: 10 }}
                      >
                        Open
                      </button>

                      <button
                        type="button"
                        className={`btn flex-fill fw-bold ${
                          action === "off"
                            ? "btn-dark"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() => setAction("off")}
                        style={{ borderRadius: 10 }}
                      >
                        Close
                      </button>
                    </div>
                  )}

                  {/* Control Access */}
                  {curtainControl === "control" && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-semibold">Curtain Position</span>

                        <strong>{curtainLevel}%</strong>
                      </div>

                      <Input
                        type="range"
                        min="0"
                        max="100"
                        step="10"
                        value={curtainLevel}
                        onChange={(e) => {
                          setCurtainLevel(Number(e.target.value));
                        }}
                        style={{
                          width: "100%",
                          cursor: "pointer",
                          accentColor: "#1c1c1e",
                        }}
                      />

                      <div className="d-flex justify-content-between mt-1 text-muted small">
                        <span>Closed</span>
                        <span>Open</span>
                      </div>
                    </div>
                  )}
                </FormGroup>
              ) : (
                /* Existing Action UI */
                !TABS_WITHOUT_ACTION.includes(deviceTypeTab) && (
               <FormGroup className="d-flex align-items-center gap-2">
                  <Label className="fw-semibold mt-2 mb-0">Action</Label>

                  <div className="form-check form-switch d-flex align-items-center gap-2 mb-0">
                    <Input
                      type="checkbox"
                      role="switch"
                      id="actionToggle"
                      className="form-check-input"
                      style={{
                        width: 42,
                        height: 22,
                        cursor: "pointer",
                      }}
                      checked={action === "on"}
                      onChange={(e) =>
                        setAction(e.target.checked ? "on" : "off")
                      }
                    />

                    <span
                      className="fw-semibold mt-1"
                      style={{
                        color: action === "on" ? "#198754" : "#dc3545",
                      }}
                    >
                      {action === "on" ? "ON" : "OFF"}
                    </span>
                  </div>
                </FormGroup>
                )
              )}

              {/* Time */}
              <FormGroup>
                <Label className="fw-semibold">Time </Label>
                <Input
                  type="time"
                  value={time}
                  step={1}
                  onChange={(e) => setTime(e.target.value)}
                  invalid={!!errors.time}
                  onClick={(e) => {
                    const target = e.target as HTMLInputElement & {
                      showPicker?: () => void;
                    };
                    target?.showPicker?.();
                  }}
                />
                {errors.time && (
                  <div className="text-danger font-size-11 mt-1">
                    {errors.time}
                  </div>
                )}
              </FormGroup>

              {/* Repeat checkbox */}
              <FormGroup check className="mb-3">
                <Input
                  type="checkbox"
                  id="isRepeat"
                  checked={isRepeat}
                  onChange={(e) => setIsRepeat(e.target.checked)}
                />
                <Label check for="isRepeat" className="ms-1">
                  Repeat weekly
                </Label>
              </FormGroup>

              {/* Days */}
              <FormGroup>
                <Label className="fw-semibold">
                  {isRepeat ? "Repeat on days" : "Run on day"}
                </Label>
                <div className="d-flex flex-wrap gap-2">
                  {DAY_OPTIONS.map((day) => {
                    const active = dayNumbers.includes(day.value);
                    return (
                      <button
                        type="button"
                        key={day.value}
                        onClick={() => toggleDay(day.value)}
                        className={`btn btn-sm ${
                          active ? "btn-dark" : "btn-outline-secondary"
                        } fw-bold`}
                        style={{ width: 52, borderRadius: 20 }}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
                {errors.dayNumbers && (
                  <div className="text-danger font-size-11 mt-1">
                    {errors.dayNumbers}
                  </div>
                )}
              </FormGroup>

              {/* Active checkbox */}
              <FormGroup check className="mb-3">
                <Input
                  type="checkbox"
                  id="isActive"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <Label check for="isActive" className="ms-1">
                  Active
                </Label>
              </FormGroup>
            </div>

            {/* Footer actions */}
            <div className="border-top p-3 d-flex gap-2 bg-white">
              <Button
                type="submit"
                color="dark"
                className="flex-fill fw-bold"
                disabled={submitting}
              >
                {submitting ? (
                  <Spinner size="sm" />
                ) : isEditMode ? (
                  "Save Changes"
                ) : (
                  "Create Schedule"
                )}
              </Button>
            </div>
          </form>
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
};

export default ScheduleFormOffcanvas;
